/**
 * Joseph coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const josephColoringPagesPack: ToilePack = {
    slug: 'joseph-coloring-pages',
    category: 'faith',
    name: 'Joseph coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'coat', label: 'The coat', subject: 'young Joseph holding out the long-sleeved coat of many colors his father Jacob has given him, open shapes in its stripes for coloring'},
        {key: 'pit', label: 'The pit', subject: 'Joseph lifted from a dry well by his brothers while a camel caravan of traders waits nearby'},
        {key: 'reunion', label: 'Reunion', subject: 'Joseph in Egyptian robes embracing his brothers in his hall in Egypt, grain sacks at their feet'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['dream sheaves', 'in prison', 'pharaoh dream'],
    directionHint: 'try: dream sheaves',
    seo: {
        title: 'Joseph Coloring Pages: The Coat, The Pit, Reunion',
        metaDescription: 'Nine Joseph coloring pages for Sunday school from Genesis 37 to 45: the coat, the pit and the reunion, in three line styles.',
        h1: 'Joseph coloring pages: the coat of many colors, the pit, the reunion',
        tagline: "Nine pages of Joseph's story in three scenes, each in three line weights.",
        intro: 'Printable Joseph coloring pages for Sunday school and Christian homeschool, from Genesis 37 to 45. Three scenes: Joseph holding out his coat of many colors, his brothers lifting him from the pit beside a caravan of traders, and Joseph in Egyptian robes embracing his brothers. Each scene comes in bold lines for young children, classic lines for ages six to nine, and fine detail for older children. Type a different scene or dream, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Joseph',
            "a children's church craft table",
            'a Christian homeschool Genesis unit',
            'a bulletin board on the story of Joseph',
        ],
        faq: [
            {q: "Which part of Joseph's story do these show?", a: 'Genesis 37 to 45: the coat, the pit, and the reunion in Egypt.'},
            {q: 'Does the coat have room to color?', a: 'Yes. The stripes of the coat are open shapes a child can fill.'},
            {q: "Can I ask for Joseph's dreams?", a: "Yes. Type the dream you want, such as the sheaves of grain or Pharaoh's dream. The request shapes the page, but a result is not promised to match it exactly."},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
